import { AlertTriangle, Loader2, Printer, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { ct600Service } from '../../utils/ct600Service'
import {
  CT600_BOX_SECTIONS,
  KNOWN_BOX_NUMBERS,
  formatBoxValue,
  type BoxFormat,
} from './ct600BoxCatalogue'

type GenericRecord = Record<string, any>

interface Props {
  formId: string
  open: boolean
  onClose: () => void
  /** Company name and period, shown in the preview header. */
  companyName?: string
  periodStart?: string
  periodEnd?: string
}

/**
 * Shows the CT600 exactly as it will reach HMRC — the real box numbers and
 * values from the backend's box mapper, not a re-derivation from wizard state.
 * Anything the user sees here is what gets submitted.
 */
export default function CT600PreviewModal({
  formId,
  open,
  onClose,
  companyName,
  periodStart,
  periodEnd,
}: Props) {
  const [boxes, setBoxes] = useState<GenericRecord | null>(null)
  const [validation, setValidation] = useState<GenericRecord | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !formId) return
    let active = true

    const load = async () => {
      setIsLoading(true)
      setError(null)
      try {
        // Fetch fresh rather than reusing anything cached — the whole point is
        // to show the CURRENT state of what would be submitted.
        const response = await ct600Service.getCt600Boxes(formId)
        if (!active) return
        const payload = (response.response as any)?.data ?? response.response
        setBoxes(payload?.boxes ?? null)
        setValidation(payload?.validation ?? null)
      } catch {
        if (active) setError('Could not load the CT600 preview. Please try again.')
      } finally {
        if (active) setIsLoading(false)
      }
    }

    void load()
    return () => {
      active = false
    }
  }, [open, formId])

  // Boxes the mapper produced that the catalogue doesn't describe. Surfacing
  // them keeps the preview honest: it never hides a value that will be filed.
  const extraBoxes = useMemo(() => {
    if (!boxes) return []
    return Object.entries(boxes)
      .filter(([box, value]) => !KNOWN_BOX_NUMBERS.has(box) && value !== '' && value != null)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
  }, [boxes])

  const errors: string[] = Array.isArray(validation?.errors) ? validation.errors : []
  const isValid = validation ? validation.valid !== false && errors.length === 0 : true

  if (!open) return null

  const periodLabel =
    periodStart && periodEnd
      ? `${formatBoxValue(periodStart, 'date')} to ${formatBoxValue(periodEnd, 'date')}`
      : null

  return (
    <div className='fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 print:static print:bg-white print:p-0'>
      <div className='my-8 w-full max-w-4xl rounded-2xl bg-white shadow-xl print:my-0 print:max-w-none print:shadow-none'>
        {/* Header */}
        <div className='flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4'>
          <div>
            <h2 className='text-lg font-bold text-gray-900'>CT600 preview</h2>
            <p className='mt-0.5 text-sm text-gray-600'>
              {companyName ? <span className='font-medium'>{companyName}</span> : 'Your return'}
              {periodLabel ? ` — ${periodLabel}` : ''}
            </p>
            <p className='mt-1 text-xs text-gray-500'>
              These are the exact box values that will be sent to HMRC.
            </p>
          </div>
          <div className='flex items-center gap-2 print:hidden'>
            <button
              type='button'
              onClick={() => window.print()}
              className='flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50'
            >
              <Printer className='h-4 w-4' /> Print
            </button>
            <button
              type='button'
              onClick={onClose}
              aria-label='Close preview'
              className='rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700'
            >
              <X className='h-5 w-5' />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className='max-h-[70vh] overflow-y-auto px-6 py-5 print:max-h-none print:overflow-visible'>
          {isLoading && (
            <div className='flex items-center justify-center gap-2 py-12 text-gray-500'>
              <Loader2 className='h-5 w-5 animate-spin' />
              <span className='text-sm'>Building your preview…</span>
            </div>
          )}

          {error && !isLoading && (
            <div className='rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700'>{error}</div>
          )}

          {!isLoading && !error && boxes && (
            <div className='space-y-6'>
              {!isValid && (
                <div className='rounded-lg border border-amber-200 bg-amber-50 p-4'>
                  <div className='flex gap-3'>
                    <AlertTriangle className='h-5 w-5 flex-shrink-0 text-amber-600' />
                    <div>
                      <p className='text-sm font-medium text-amber-900'>
                        HMRC may reject this return as it stands
                      </p>
                      <ul className='mt-1 list-disc space-y-0.5 pl-4 text-xs text-amber-800'>
                        {errors.map((e, i) => (
                          <li key={i}>{e}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {CT600_BOX_SECTIONS.map(section => {
                // Only render boxes the mapper actually produced, so the preview
                // reflects this return rather than a blank form.
                const rows = section.boxes.filter(
                  b => boxes[b.box] !== undefined && boxes[b.box] !== '' && boxes[b.box] !== null
                )
                if (rows.length === 0) return null

                return (
                  <section key={section.title}>
                    <h3 className='mb-2 text-sm font-semibold uppercase tracking-wide text-gray-700'>
                      {section.title}
                    </h3>
                    {section.hint && <p className='mb-2 text-xs text-gray-500'>{section.hint}</p>}
                    <div className='overflow-hidden rounded-lg border border-gray-200'>
                      <table className='w-full text-sm'>
                        <tbody>
                          {rows.map((definition, index) => (
                            <tr
                              key={definition.box}
                              className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}
                            >
                              <td className='w-16 px-3 py-2.5 align-top'>
                                <span className='inline-flex min-w-[2.5rem] justify-center rounded bg-gray-900 px-2 py-0.5 text-xs font-semibold text-white'>
                                  {definition.box}
                                </span>
                              </td>
                              <td className='px-3 py-2.5 align-top text-gray-700'>{definition.label}</td>
                              <td className='w-40 whitespace-nowrap px-3 py-2.5 text-right align-top font-semibold text-gray-900'>
                                {formatBoxValue(boxes[definition.box], definition.format as BoxFormat)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )
              })}

              {extraBoxes.length > 0 && (
                <section>
                  <h3 className='mb-2 text-sm font-semibold uppercase tracking-wide text-gray-700'>
                    Other boxes
                  </h3>
                  <div className='overflow-hidden rounded-lg border border-gray-200'>
                    <table className='w-full text-sm'>
                      <tbody>
                        {extraBoxes.map(([box, value], index) => (
                          <tr key={box} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}>
                            <td className='w-16 px-3 py-2.5 align-top'>
                              <span className='inline-flex min-w-[2.5rem] justify-center rounded bg-gray-900 px-2 py-0.5 text-xs font-semibold text-white'>
                                {box}
                              </span>
                            </td>
                            <td className='px-3 py-2.5 align-top text-gray-500'>Box {box}</td>
                            <td className='w-40 whitespace-nowrap px-3 py-2.5 text-right align-top font-semibold text-gray-900'>
                              {String(value)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className='flex items-center justify-between gap-3 border-t border-gray-200 px-6 py-4 print:hidden'>
          <p className='text-xs text-gray-500'>
            Nothing is sent to HMRC until you submit on the previous screen.
          </p>
          <button
            type='button'
            onClick={onClose}
            className='rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800'
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
